import { z } from 'zod';
import { CurrencyCodeSchema, DateOnlySchema, IdSchema, ListQuerySchema, MoneyAmountSchema, PageInfoSchema, TimestampSchema, VersionSchema, } from './common.js';
export const ClaimStatusSchema = z.enum(['reported', 'open', 'approved', 'denied', 'closed']);
export const ClaimPartyRoleSchema = z.enum(['adjuster', 'examiner', 'agent', 'public_adjuster']);
/**
 * Optional `insurance_claims` capability (specification 8.7). The carrier is a canonical
 * company. `deductible` is present only for callers holding insurance_claims.manage.
 */
export const InsuranceClaimSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    lossEventId: IdSchema,
    carrierCompanyId: IdSchema.nullable(),
    policyNumber: z.string().nullable(),
    claimNumber: z.string().nullable(),
    status: ClaimStatusSchema,
    deductible: MoneyAmountSchema.nullable().optional(),
    deductibleCurrency: CurrencyCodeSchema.nullable().optional(),
    dateOfLoss: DateOnlySchema.nullable(),
    reportedToCarrierAt: DateOnlySchema.nullable(),
    approvedAt: DateOnlySchema.nullable(),
    closedAt: DateOnlySchema.nullable(),
    notes: z.string().nullable(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CreateInsuranceClaimRequestSchema = z.strictObject({
    lossEventId: IdSchema,
    carrierCompanyId: IdSchema.nullable().default(null),
    policyNumber: z.string().trim().max(100).nullable().default(null),
    claimNumber: z.string().trim().max(100).nullable().default(null),
    status: ClaimStatusSchema.default('reported'),
    deductible: MoneyAmountSchema.nullable().default(null),
    deductibleCurrency: CurrencyCodeSchema.nullable().default(null),
    dateOfLoss: DateOnlySchema.nullable().default(null),
    reportedToCarrierAt: DateOnlySchema.nullable().default(null),
    approvedAt: DateOnlySchema.nullable().default(null),
    closedAt: DateOnlySchema.nullable().default(null),
    notes: z.string().trim().max(10_000).nullable().default(null),
});
export const UpdateInsuranceClaimRequestSchema = CreateInsuranceClaimRequestSchema.omit({
    lossEventId: true,
})
    .partial()
    .extend({ version: VersionSchema });
export const ClaimPartySchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    claimId: IdSchema,
    contactId: IdSchema,
    role: ClaimPartyRoleSchema,
    effectiveFrom: DateOnlySchema.nullable(),
    effectiveTo: DateOnlySchema.nullable(),
    notes: z.string().nullable(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CreateClaimPartyRequestSchema = z
    .strictObject({
    contactId: IdSchema,
    role: ClaimPartyRoleSchema,
    effectiveFrom: DateOnlySchema.nullable().default(null),
    effectiveTo: DateOnlySchema.nullable().default(null),
    notes: z.string().trim().max(1000).nullable().default(null),
})
    .refine((value) => !value.effectiveFrom || !value.effectiveTo || value.effectiveFrom <= value.effectiveTo, { message: 'effectiveTo must not be before effectiveFrom', path: ['effectiveTo'] });
export const ClaimPartyListSchema = z.strictObject({
    items: z.array(ClaimPartySchema),
    page: PageInfoSchema,
});
export const InsuranceClaimListQuerySchema = ListQuerySchema.extend({
    lossEventId: IdSchema.optional(),
    carrierCompanyId: IdSchema.optional(),
    status: ClaimStatusSchema.optional(),
});
export const InsuranceClaimListSchema = z.strictObject({
    items: z.array(InsuranceClaimSchema),
    page: PageInfoSchema,
});
/** Uppercase alphanumerics only: "CLM 12-345 x" and "clm12345X" are the same claim. */
export function normalizeClaimNumber(value) {
    if (!value)
        return null;
    const normalized = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    return normalized === '' ? null : normalized.slice(0, 100);
}
